import { ComponentRef, Injectable, NgZone } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DynamicComponentService } from '../dynamic-component/dynamic-component.service';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogMode,
    ConfirmationDialogTone,
} from './confirmation-dialog.component';

export interface ConfirmationOptions {
    title?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    tone?: ConfirmationDialogTone;
}

@Injectable({
    providedIn: 'root',
})
export class ConfirmationService {
    constructor(
        private readonly dynamicComponentService: DynamicComponentService,
        private readonly ngZone: NgZone,
    ) { }

    ok(message: string, options?: ConfirmationOptions): Observable<void> {
        return this.openDialog('ok', message, {
            title: options?.title ?? 'Aviso',
            confirmButtonText: options?.confirmButtonText ?? 'OK',
            tone: options?.tone ?? 'info',
        }).pipe(map(() => undefined));
    }

    Ok(message: string, options?: ConfirmationOptions): Observable<void> {
        return this.ok(message, options);
    }

    warning(message: string, options?: ConfirmationOptions): Observable<void> {
        return this.openDialog('ok', message, {
            title: options?.title ?? 'Atenção',
            confirmButtonText: options?.confirmButtonText ?? 'Entendi',
            tone: 'warning',
        }).pipe(map(() => undefined));
    }

    error(message: string, options?: ConfirmationOptions): Observable<void> {
        return this.openDialog('ok', message, {
            title: options?.title ?? 'Erro',
            confirmButtonText: options?.confirmButtonText ?? 'Fechar',
            tone: 'error',
        }).pipe(map(() => undefined));
    }

    success(message: string, options?: ConfirmationOptions): Observable<void> {
        return this.openDialog('ok', message, {
            title: options?.title ?? 'Sucesso',
            confirmButtonText: options?.confirmButtonText ?? 'OK',
            tone: 'success',
        }).pipe(map(() => undefined));
    }

    yesNo(message: string, options?: ConfirmationOptions): Observable<boolean> {
        return this.openDialog('yes-no', message, {
            title: options?.title ?? 'Confirmação',
            confirmButtonText: options?.confirmButtonText ?? 'Sim',
            cancelButtonText: options?.cancelButtonText ?? 'Não',
            tone: options?.tone ?? 'info',
        });
    }

    YesNo(message: string, options?: ConfirmationOptions): Observable<boolean> {
        return this.yesNo(message, options);
    }

    private openDialog(
        mode: ConfirmationDialogMode,
        message: string,
        options: ConfirmationOptions,
    ): Observable<boolean> {
        return new Observable<boolean>((subscriber) => {
            const componentRef = this.dynamicComponentService.appendComponentToBody(ConfirmationDialogComponent);
            const instance = componentRef.instance;
            let isDestroyed = false;

            instance.visible = true;
            instance.mode = mode;
            instance.message = message;
            instance.title = options.title ?? (mode === 'ok' ? 'Aviso' : 'Confirmação');
            instance.tone = options.tone ?? (mode === 'ok' ? 'info' : 'warning');
            instance.confirmButtonText = options.confirmButtonText ?? (mode === 'ok' ? 'OK' : 'Sim');
            instance.cancelButtonText = options.cancelButtonText ?? 'Não';

            const subscription = instance.resolved.subscribe((confirmed) => {
                this.ngZone.run(() => {
                    if (!isDestroyed) {
                        this.destroyComponent(componentRef);
                        isDestroyed = true;
                    }

                    subscriber.next(confirmed);
                    subscriber.complete();
                });
            });

            return () => {
                subscription.unsubscribe();
                if (!isDestroyed) {
                    this.destroyComponent(componentRef);
                    isDestroyed = true;
                }
            };
        });
    }

    private destroyComponent(componentRef: ComponentRef<ConfirmationDialogComponent>): void {
        this.dynamicComponentService.removeComponent(componentRef);
    }
}